/**
 * Billing Warning Banner
 * ======================
 * 
 * Zeigt faire Billing-Warnungen basierend auf Status:
 * 
 * - OVERDUE_WARNING (gelb): "Zahlung offen. Bitte bis DD.MM bezahlen."
 * - GRACE (orange): "⚠️ Sperre in X Tagen!"
 * - SOFT_LOCKED (rot): "🚫 Neue Verkäufe gesperrt"
 * 
 * NICHT angezeigt für: ACTIVE, FREE, HARD_LOCKED
 */

import React from 'react';
import { AlertTriangle, X, CreditCard, Clock, AlertCircle, Gift } from 'lucide-react';
import { Button } from "@/components/ui/button";

const BillingWarningBanner = ({ billingWarning, onDismiss }) => {
  // Nicht anzeigen wenn:
  // - Kein Warning
  // - Status ist FREE (Gratis-Plan)
  // - Bereits dismissed (wird von Parent gehandhabt)
  if (!billingWarning) return null;
  
  const { type, message, days_overdue, days_until_lock, can_sell, payment_due_date } = billingWarning;
  
  // Styling basierend auf Status
  const getStatusConfig = () => {
    switch (type) {
      case 'overdue_warning':
        return {
          bgColor: 'bg-amber-500/10 border-amber-500/30',
          textColor: 'text-amber-200',
          iconColor: 'text-amber-400',
          icon: Clock,
          title: 'Zahlung offen',
          showDaysUntilLock: true
        };
      case 'grace':
        return {
          bgColor: 'bg-orange-500/15 border-orange-500/40',
          textColor: 'text-orange-200',
          iconColor: 'text-orange-400',
          icon: AlertTriangle,
          title: 'Letzte Warnung',
          showDaysUntilLock: true
        };
      case 'soft_locked':
        return {
          bgColor: 'bg-red-500/15 border-red-500/40',
          textColor: 'text-red-200',
          iconColor: 'text-red-400',
          icon: AlertCircle,
          title: 'Account eingeschränkt',
          showDaysUntilLock: false
        };
      default:
        // payment_warning (legacy)
        return {
          bgColor: 'bg-amber-500/10 border-amber-500/30',
          textColor: 'text-amber-200',
          iconColor: 'text-amber-400',
          icon: Clock,
          title: 'Hinweis',
          showDaysUntilLock: true
        };
    }
  };
  
  const config = getStatusConfig();
  const IconComponent = config.icon;
  
  // Format due date
  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return null;
    }
  };
  
  const formattedDueDate = formatDueDate(payment_due_date);
  
  return (
    <div 
      data-testid="billing-warning-banner"
      className={`mb-6 rounded-lg border ${config.bgColor} p-4`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 ${config.iconColor}`}>
            <IconComponent className="h-5 w-5" />
          </div>
          
          <div className="flex-1">
            <h4 className={`font-semibold ${config.textColor}`}>
              {config.title}
            </h4>
            
            <p className={`mt-1 text-sm ${config.textColor} opacity-90`}>
              {message}
            </p>
            
            {/* Status Details */}
            <div className="mt-2 flex flex-wrap gap-4 text-xs">
              {days_overdue > 0 && (
                <span className={config.textColor}>
                  <strong>{days_overdue}</strong> Tag{days_overdue !== 1 ? 'e' : ''} überfällig
                </span>
              )}
              
              {config.showDaysUntilLock && days_until_lock > 0 && (
                <span className={`${config.textColor} font-medium`}>
                  Sperre in <strong>{days_until_lock}</strong> Tag{days_until_lock !== 1 ? 'en' : ''}
                </span>
              )}
              
              {formattedDueDate && (
                <span className={config.textColor}>
                  Fällig: <strong>{formattedDueDate}</strong>
                </span>
              )}
              
              {can_sell === false && (
                <span className="text-red-300 font-medium">
                  🚫 Neue Verkäufe gesperrt
                </span>
              )}
            </div>
            
            {/* Action Button für Bezahlung */}
            {can_sell !== false && (
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500/50 text-amber-300 hover:bg-amber-500/20 hover:text-amber-200"
                  onClick={() => window.open('/app/einstellungen?tab=billing', '_blank')}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Jetzt bezahlen
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {/* Dismiss Button - nur für Warning/Grace, nicht für Soft Lock */}
        {type !== 'soft_locked' && onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className={`${config.textColor} hover:bg-white/10 -mt-1 -mr-1`}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default BillingWarningBanner;
